import { useCallback, useEffect, useMemo, useState } from 'react';
import BackendService from '../../../services/admin/BackendService';
import Contact from '../../../models/Contact';
import { openInNewTab, redirectTo } from '../../../utils/navigation';
import { isMobileDevice } from './impersonateDeviceDetect';

export interface Handoff {
  token: string;
  expiresAt: number;
}

/**
 * The impersonation origin overrides below are local-dev only: script/run_for.sh sets them and
 * deployed (webpack) builds never define them, so an unguarded `process.env.X` would stay in the
 * bundle and throw "process is not defined". NODE_ENV is defined by every build.
 */
const isLocalDev = (): boolean => process.env.NODE_ENV === 'development';

/**
 * The backend builds the impersonation link from its own END_USER_FRONTEND_URL, so a local admin
 * pointed at the staging backend would open the deployed staging investor site. `npm run
 * start:admin:staging-dev` (script/run_for.sh) sets REACT_APP_IMPERSONATION_REDIRECT_ORIGIN to the
 * local investor app; nothing else sets it, so deployed builds keep the backend's link unchanged.
 */
export function impersonationBrowserUrl(redirectUrl: string): string {
  const localOrigin = isLocalDev()
    ? process.env.REACT_APP_IMPERSONATION_REDIRECT_ORIGIN
    : undefined;
  if (!localOrigin) return redirectUrl;
  const url = new URL(redirectUrl);
  const local = new URL(localOrigin);
  url.protocol = local.protocol;
  url.host = local.host;
  return url.toString();
}

/**
 * The QR / deep-link target for an app handoff. Normally this admin site's own origin; a local
 * admin run with `--expose` (script/run_for.sh) sets REACT_APP_IMPERSONATION_HANDOFF_ORIGIN to the
 * machine's LAN address, because a phone scanning the QR cannot open localhost.
 */
export function handoffUrl(token: string): string {
  const lanOrigin = isLocalDev() ? process.env.REACT_APP_IMPERSONATION_HANDOFF_ORIGIN : undefined;
  const origin = lanOrigin || window.location.origin;
  return `${origin}/impersonate/app?ht=${encodeURIComponent(token)}`;
}

/** Seconds until a generated handoff token expires, ticking each second; 0 with no handoff. */
export function useHandoffCountdown(handoff: Handoff | null): number {
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!handoff) {
      setSecondsLeft(0);
      return undefined;
    }
    const tick = () => setSecondsLeft(Math.round((handoff.expiresAt - Date.now()) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [handoff]);

  return secondsLeft;
}

/**
 * State + backend calls behind the Impersonate dialog: starting a browser
 * impersonation, starting an app handoff (QR on desktop, deep link on mobile),
 * and whether the session should capture the client's logs (sent with either
 * start, carried by the code/handoff).
 */
export function useImpersonateHandoff(
  contact: Contact | null,
  open: boolean,
  onClose: () => void,
  onError: (message: string) => void,
) {
  const isMobile = useMemo(() => isMobileDevice(), []);
  const [busy, setBusy] = useState<'browser' | 'app' | null>(null);
  const [handoff, setHandoff] = useState<Handoff | null>(null);
  const secondsLeft = useHandoffCountdown(handoff);
  const [captureLogs, setCaptureLogs] = useState(false);

  useEffect(() => {
    if (open) return;
    setBusy(null);
    setHandoff(null);
    setCaptureLogs(false);
  }, [open]);

  const fail = useCallback(
    (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unknown error';
      // Logged as well as shown, so a "Capture logs" session records why the start failed.
      console.error('Failed to start impersonation', error);
      onError(`Failed to start impersonation: ${message}`);
    },
    [onError],
  );

  const openInBrowser = useCallback(() => {
    if (!contact) return;
    setBusy('browser');
    new BackendService()
      .startImpersonation(contact, { captureLogs })
      .then(({ redirectUrl }) => {
        if (!redirectUrl) return;
        (isMobile ? redirectTo : openInNewTab)(impersonationBrowserUrl(redirectUrl));
        onClose();
      })
      .catch(fail)
      .finally(() => setBusy(null));
  }, [contact, isMobile, onClose, fail, captureLogs]);

  const openInApp = useCallback(() => {
    if (!contact) return;
    setBusy('app');
    new BackendService()
      .startImpersonationApp(contact, { captureLogs })
      .then(({ handoffToken, expiresAt }) => {
        if (isMobile) {
          redirectTo(handoffUrl(handoffToken));
          onClose();
          return;
        }
        setHandoff({ token: handoffToken, expiresAt: new Date(expiresAt).getTime() });
      })
      .catch(fail)
      .finally(() => setBusy(null));
  }, [contact, isMobile, onClose, fail, captureLogs]);

  return {
    isMobile,
    busy,
    handoff,
    secondsLeft,
    expired: handoff != null && secondsLeft <= 0,
    captureLogs,
    setCaptureLogs,
    openInBrowser,
    openInApp,
  };
}
